// src/lib/lead/extraction/textHeuristics.ts
import type { ExtractedLeadV2, LockedFields } from "../../../types/lead";
import { sanitizeCustomerPhone } from "@/lib/lead/phones";
import {
  extractEmailFromText,
  extractFullNameFromText,
  extractPhoneCandidateFromText,
  extractProductSubtypeFromText,
  extractSizeOptionsFromText,
  isValidEmail,
  mergePreferExistingNonBlank,
  normalizeEmail,
  normalizeName,
  normalizePhone,
  preferNonEmpty,
  stripLockedFields,
} from "./textHeuristicsHelpers";
import {
  type PaymentAmbiguous,
  consolidateBudget,
  extractBudgetCandidatesFromText,
  shouldClearBudgetAsPhoneFragment,
} from "./textHeuristicsBudget";

export type { PaymentAmbiguous };

export interface TextHeuristics {
  normalizePhone: (s: string | null | undefined) => string;
  isValidEmail: (email: string | null | undefined) => boolean;
  extractPhoneCandidateFromText: (text: string) => string | null;
  extractEmailFromText: (text: string) => string | null;
  extractFullNameFromText: (text: string) => { first: string; last: string } | null;
  extractSizeOptionsFromText: (text: string) => string[];
  extractProductSubtypeFromText: (text: string) => string | null;
  extractBudgetCandidatesFromText: (
    text: string
  ) => { cash?: string; monthly?: string; paymentAmbiguous?: PaymentAmbiguous };
  consolidateBudget: (
    cash?: string | null,
    monthly?: string | null,
    paymentAmbiguous?: PaymentAmbiguous
  ) => string | null;
  shouldClearBudgetAsPhoneFragment: (budget: string | null, phone: string | null, transcript?: string) => boolean;
  sanitizeCustomerPhone: typeof sanitizeCustomerPhone;
  stripLockedFields: (incoming: ExtractedLeadV2, locked?: LockedFields) => Partial<ExtractedLeadV2>;
  mergePreferExistingNonBlank: <T extends Record<string, any>>(base: T, incoming: Partial<T>) => T;
}

export const textHeuristics: TextHeuristics = {
  normalizePhone,
  isValidEmail,
  extractPhoneCandidateFromText,
  extractEmailFromText,
  extractFullNameFromText,
  extractSizeOptionsFromText,
  extractProductSubtypeFromText,
  extractBudgetCandidatesFromText,
  consolidateBudget,
  shouldClearBudgetAsPhoneFragment,
  sanitizeCustomerPhone,
  stripLockedFields,
  mergePreferExistingNonBlank,
};

export { normalizeEmail, normalizeName, preferNonEmpty };
